import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";

// How the PAA change notes are actually landing, from the office's own filings.
//
// WHY THIS SCREEN EXISTS. The written spec for how these notes should read was
// largely AI-generated and has since been walked back, so the only trustworthy
// source is what people actually change on real filings. Every version of every
// note is recorded (PaaNoteRevision); this is where it becomes readable.
//
// IT IS SHAPED AS HISTORIES, NOT CORRECTIONS. A flat list of "we said X, they
// filed Y" answers "was it wrong" and nothing else. It cannot tell apart the two
// cases that decide what to fix:
//
//   written → operator corrected the summary → REWRITTEN → filed unchanged
//       the engine had the right machinery and the wrong story. Fix the prompt.
//
//   written → rewritten → still hand-edited
//       the story was fine and the writing was not. Fix how notes are written.
//
// Hence "rewriting fixed it" as a headline number, and the full version list
// under each row.
//
// AND IT LEADS WITH THE ACCEPT RATE, not with the corrections. A table of
// nothing but corrections reads as "the engine is always wrong" — the notes
// taken as written are the larger, quieter half and they are what the rate is
// measured against.

interface NoteVersion {
  sequence: number;
  text: string;
  source: "Engine" | "Operator";
  // Mirrors PaaNoteTrigger. OperatorEdit was missing here and from the switch
  // below, so a committed edit rendered a blank label with a stray separator
  // after it — the version list's own version of an empty finding.
  trigger: "FirstRun" | "RowRecompare" | "Rewrite" | "Filed" | "OperatorEdit";
  createdAt: string;
}

interface NoteHistory {
  sheetId: string;
  address: string | null;
  jobNumber: string | null;
  filedAt: string;
  gist: string | null;
  versions: NoteVersion[];
}

interface NoteReview {
  filed: number;
  acceptedAsWritten: number;
  reworded: number;
  cleared: number;
  rewrittenThenAccepted: number;
  rewrittenThenEdited: number;
  truncated: boolean;
  histories: NoteHistory[];
}

type Filter = "changed" | "all";

export function NoteReviewSection() {
  const [filter, setFilter] = useState<Filter>("changed");

  const review = useQuery<NoteReview>({
    queryKey: ["dashboard", "paa-notes"],
    queryFn: () => api<NoteReview>("/api/dashboard/paa-notes"),
  });

  if (review.isLoading || review.isError || !review.data) return null;
  const data = review.data;

  // Nothing filed yet is the normal state early on, and saying so plainly beats
  // an empty table that reads like a broken query.
  if (data.filed === 0) {
    return (
      <section className="mt-8 overflow-hidden rounded-lg border border-rule bg-surface shadow-soft">
        <Header />
        <p className="px-6 py-5 text-[0.88rem] leading-relaxed text-ink-soft">
          No change notes have been filed yet. Every version of every note is
          recorded from the first comparison onward — the first AI1 you generate
          with an approved set will show up here.
        </p>
      </section>
    );
  }

  const changed = data.reworded + data.cleared;
  const rewritten = data.rewrittenThenAccepted + data.rewrittenThenEdited;
  const shown =
    filter === "changed"
      ? data.histories.filter((h) => wasChanged(h))
      : data.histories;

  return (
    <section className="mt-8 overflow-hidden rounded-lg border border-rule bg-surface shadow-soft">
      <Header />

      <div className="flex flex-wrap gap-x-10 gap-y-4 border-b border-rule px-6 py-5">
        <Stat
          label="Taken as written"
          value={`${pct(data.acceptedAsWritten, data.filed)}%`}
          hint={`${data.acceptedAsWritten} of ${data.filed} filed notes`}
        />
        <Stat
          label="Reworded"
          value={String(data.reworded)}
          hint="the engine wrote something and it was changed"
        />
        <Stat
          label="Deleted"
          value={String(data.cleared)}
          hint="the strongest “this was wrong” there is"
        />
        {rewritten > 0 && (
          // The question a pair of before/after texts could never answer.
          <Stat
            label="Rewriting fixed it"
            value={`${pct(data.rewrittenThenAccepted, rewritten)}%`}
            hint={`${data.rewrittenThenAccepted} of ${rewritten} rewritten notes were then filed untouched`}
          />
        )}
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-rule px-6 py-3">
        <div className="flex gap-1.5">
          <Toggle active={filter === "changed"} onClick={() => setFilter("changed")}>
            Changed ({changed})
          </Toggle>
          <Toggle active={filter === "all"} onClick={() => setFilter("all")}>
            All ({data.histories.length})
          </Toggle>
        </div>
        {data.truncated && (
          // Never let a clipped window masquerade as a complete rate.
          <span className="text-[0.78rem] text-ink-muted">
            Showing the most recent activity only — the rates above cover what was
            scanned, not all time.
          </span>
        )}
      </div>

      <ul className="divide-y divide-rule">
        {shown.map((h, i) => (
          <HistoryRow key={`${h.sheetId}-${h.filedAt}-${i}`} history={h} />
        ))}
      </ul>

      {shown.length === 0 && (
        <p className="px-6 py-5 text-[0.88rem] text-ink-soft">
          Every filed note was taken exactly as written.
        </p>
      )}
    </section>
  );
}

function Header() {
  return (
    <header className="flex flex-wrap items-start justify-between gap-x-6 gap-y-2 border-b border-rule px-6 py-3.5">
      <div>
        <div className="text-[0.95rem] font-semibold text-ink">Change notes</div>
        <p className="mt-1 text-[0.82rem] leading-relaxed text-ink-muted">
          What the engine wrote, what was filed, and what happened in between.
        </p>
      </div>
      {/* The screen above is a summary — capped, and without the evidence each
          note was written against. This is the whole log, and it is the reason
          any of it is recorded: notes only get better if someone can read back
          what the office actually changed. A plain link because the endpoint is
          a cookie-authenticated file download; no fetch, no blob, nothing to go
          wrong between the click and the file.

          Deliberately in the shared header, so it is also there when nothing has
          been filed yet — the log holds edits and re-comparisons that the
          filings-only view above never shows. */}
      <div className="flex shrink-0 flex-wrap gap-2">
        <a
          href="/api/dashboard/paa-notes/export"
          download
          className="rounded-lg border border-rule px-2.5 py-1.5 text-[0.8rem] font-medium text-ink-soft transition-colors hover:border-primary hover:text-primary"
        >
          Export every version
        </a>
        {/* The other half, and the half the log above cannot supply: a revision
            says a note was CHANGED, only a report says why — or that a change
            was missed, which no edit can express at all. A zip, because each
            report carries the two drawings it is a claim about. */}
        <a
          href="/api/dashboard/paa-reports/export"
          download
          className="rounded-lg border border-rule px-2.5 py-1.5 text-[0.8rem] font-medium text-ink-soft transition-colors hover:border-primary hover:text-primary"
        >
          Reported problems
        </a>
      </div>
    </header>
  );
}

// One sheet's note, from first attempt to filing. Collapsed to the two versions
// that matter; the full chain is one click away, because on most rows the chain
// is two entries long and a permanent tree would be noise.
function HistoryRow({ history }: { history: NoteHistory }) {
  const [open, setOpen] = useState(false);

  const filed = history.versions[history.versions.length - 1];
  const firstEngine = history.versions.find((v) => v.source === "Engine");
  const changed = wasChanged(history);

  return (
    <li className="px-6 py-4">
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <span className="font-mono text-[0.84rem] font-medium text-ink">
          {history.sheetId}
        </span>
        <span className="min-w-0 flex-1 truncate text-[0.8rem] text-ink-muted">
          {[history.address, history.jobNumber].filter(Boolean).join(" · ")}
        </span>
        <span className="text-[0.78rem] text-ink-muted">{fmtDate(history.filedAt)}</span>
      </div>

      {changed && firstEngine ? (
        <div className="mt-2 space-y-1.5 text-[0.85rem] leading-relaxed">
          <Line label="We wrote" text={firstEngine.text} muted />
          <Line label="Filed" text={filed.text} />
        </div>
      ) : (
        <p className="mt-2 text-[0.85rem] leading-relaxed text-ink-soft">
          {filed.text || <span className="text-ink-muted">(no note)</span>}
        </p>
      )}

      {history.versions.length > 2 && (
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          aria-expanded={open}
          className="mt-2 text-[0.78rem] font-medium text-ink-muted underline decoration-rule-strong underline-offset-4 hover:text-primary hover:decoration-primary"
        >
          {open ? "Hide" : `All ${history.versions.length} versions`}
        </button>
      )}

      {open && (
        <ol className="mt-2.5 space-y-2 border-l-2 border-rule pl-3.5">
          {history.versions.map((v) => (
            <li key={v.sequence} className="text-[0.82rem] leading-relaxed">
              <div className="text-[0.72rem] uppercase tracking-wide text-ink-muted">
                {triggerLabel(v.trigger)} · {v.source === "Engine" ? "engine" : "operator"}
              </div>
              <div className="text-ink-soft">
                {v.text || <span className="text-ink-muted">(no note)</span>}
              </div>
            </li>
          ))}
          {history.gist && (
            // The story in force when these were written. Without it a note is
            // uninterpretable — you cannot tell bad phrasing from a correct
            // sentence about the wrong thing.
            <li className="pt-1 text-[0.78rem] leading-relaxed text-ink-muted">
              <span className="uppercase tracking-wide">Summary at filing</span>
              <div>{history.gist}</div>
            </li>
          )}
        </ol>
      )}
    </li>
  );
}

function Line({ label, text, muted }: { label: string; text: string; muted?: boolean }) {
  return (
    <div className="flex flex-wrap gap-x-2">
      <span className="shrink-0 text-[0.72rem] uppercase tracking-wide text-ink-muted">
        {label}
      </span>
      <span className={cn("min-w-0 flex-1", muted ? "text-ink-muted" : "text-ink")}>
        {text || <span className="text-ink-muted">(no note)</span>}
      </span>
    </div>
  );
}

function Stat({ label, value, hint }: { label: string; value: string; hint: string }) {
  return (
    <div>
      <div className="label text-ink-muted">{label}</div>
      <div className="mt-0.5 text-[1.6rem] font-medium tracking-tight text-ink tabular-nums">
        {value}
      </div>
      <div className="mt-0.5 text-[0.78rem] text-ink-muted">{hint}</div>
    </div>
  );
}

function Toggle({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "rounded-lg px-2.5 py-1 text-[0.82rem] font-medium transition-colors",
        active
          ? "bg-primary-soft text-ink"
          : "text-ink-muted hover:bg-surface-2 hover:text-ink",
      )}
    >
      {children}
    </button>
  );
}

// A note is "changed" when the filed text is not the engine's. Decided by the
// recorded authorship rather than by comparing strings here, so the screen and
// the stored data can never disagree.
function wasChanged(history: NoteHistory): boolean {
  const filed = history.versions[history.versions.length - 1];
  return filed?.source === "Operator";
}

function triggerLabel(trigger: NoteVersion["trigger"]): string {
  switch (trigger) {
    case "FirstRun":
      return "First comparison";
    case "RowRecompare":
      return "Re-compared";
    case "Rewrite":
      return "Rewritten after a summary edit";
    case "Filed":
      return "Filed";
    case "OperatorEdit":
      return "Edited";
  }
}

function pct(part: number, whole: number): number {
  return whole === 0 ? 0 : Math.round((part / whole) * 100);
}

function fmtDate(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? ""
    : d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}
